import { useEffect, useState, useMemo } from "react";
import Tree from "../../components/jsonSchemaEditor"
import JsonView from '@uiw/react-json-view';
import { JSONSchema7 } from "json-schema";
import { Button, Pagination, Spinner, Tab, Tabs, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from "@nextui-org/react";
import { SavedTestRun } from "../../types/types";
import HTMLView from "../../components/htmlView";
import { useFirestore, useFirestoreCollectionData, useStorage } from "reactfire";
import { collection, deleteDoc, doc, orderBy, query } from "firebase/firestore";
import { ref, getDownloadURL } from 'firebase/storage';
import { AiOutlineDelete } from "react-icons/ai";

export interface SavedTestRunsProps {
    extractorId: string;
}

export const SavedTestRuns: React.FC<SavedTestRunsProps> = ({ extractorId }) => {
    const [selectedTestRun, setSelectedTestRun] = useState<SavedTestRun | null>(null);
    const [downloadUrl, setDownloadUrl] = useState<string>("");
    
    const firestore = useFirestore();
    const storage = useStorage();

    const { data: testRuns } = useFirestoreCollectionData(
        query(collection(firestore, `extractors/${extractorId}/testruns`), orderBy('createdAt', "desc")), 
        { idField: 'id' }
    );

    const dateFormatter = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });

    const [page, setPage] = useState(1);
    const rowsPerPage = 5;

    const pages = Math.ceil((testRuns ? testRuns.length : 0) / rowsPerPage);

    const items = useMemo(() => {
        if (testRuns) {
            const start = (page - 1) * rowsPerPage;
            const end = start + rowsPerPage;
            return testRuns.slice(start, end);
        }
        return [];
    }, [page, testRuns]);

    useEffect(() => {
        async function fetchDownloadUrl() {
            if (selectedTestRun?.output?.excel) {
                const fileRef = ref(storage, selectedTestRun.output.excel);
                try {
                    const url = await getDownloadURL(fileRef);
                    setDownloadUrl(url);
                } catch (error) {
                    console.error("Error fetching download URL:", error);
                    setDownloadUrl("");
                }
            } else {
                setDownloadUrl("");
            }
        }
        fetchDownloadUrl()
    }, [selectedTestRun, storage]);

    const _showOutputs = () => {
        if (selectedTestRun?.output) {
            return (
                <div className="h-full">
                    <Tabs aria-label="Options">
                        <Tab key="json" title="JSON">
                            {selectedTestRun.output.json ? <JsonView value={selectedTestRun.output.json} /> : <p>Failed to parse JSON</p>}
                        </Tab>
                        <Tab key="html" title="HTML">
                            {selectedTestRun.output.html ? <HTMLView html={selectedTestRun.output.html} /> : <p>Failed to parse HTML</p>}
                            {downloadUrl && downloadUrl != "" ?
                                <a href={downloadUrl}>
                                    <div className="rounded-md mt-[20px] p-[10px] bg-blue-500 fit-content w-fit text-white">
                                        Download Excel
                                    </div>
                                </a> : null}
                        </Tab>
                    </Tabs>
                </div >
            )
        }
    }

    const handleTestRunSelect = (testRun: SavedTestRun) => {
        setSelectedTestRun(testRun);
    }

    const handleDelete = async (testRunId: string) => {
        try {
            await deleteDoc(doc(firestore, `extractors/${extractorId}/testruns/${testRunId}`));
            // Clear selected test run if it was deleted
            if (selectedTestRun?.id === testRunId) {
                setSelectedTestRun(null);
            }
        } catch (error) {
            console.error("Error deleting test run:", error);
        }
    }

    if (!testRuns) {
        return (
            <div className="flex w-full h-full justify-center items-center">
                <Spinner />
            </div>
        )
    }

    return (
        <div className="flex w-full min-h-[65vh]">
            <div className="flex columns-2 gap-5 w-full">
                <div className="flex-1 overflow-y-auto p-4 bg-base-100">
                    <div className="mt-2 mb-4">
                        <div className="text-lg font-bold">Saved Test Runs</div>
                        <span className="text-sm">Select a test run to view its details</span>
                    </div>
                    <Table
                        aria-label="Saved Test Runs"
                        removeWrapper
                        selectionMode="single"
                        onRowAction={(key) => {
                            const testRun = testRuns.find(tr => tr.id === key) as SavedTestRun;
                            handleTestRunSelect(testRun);
                        }}
                        bottomContent={
                            <div className="flex w-full justify-center">
                                <Pagination
                                    isCompact
                                    showControls
                                    showShadow
                                    color="secondary"
                                    page={page}
                                    total={pages}
                                    onChange={(page) => setPage(page)}
                                />
                            </div>
                        }
                        classNames={{
                            wrapper: "min-h-[222px]",
                        }}
                    >
                        <TableHeader>
                            <TableColumn>Filename</TableColumn>
                            <TableColumn>Created At</TableColumn>
                            <TableColumn width={50}>Actions</TableColumn>
                        </TableHeader>
                        <TableBody
                            emptyContent={"No saved test runs"}
                            items={items}>
                            {(item) => {
                                const testRun = item as SavedTestRun;
                                return (
                                    <TableRow key={testRun.id}>
                                        <TableCell>{testRun.testFile?.name || 'N/A'}</TableCell>
                                        <TableCell>
                                            {testRun.createdAt ? dateFormatter.format(new Date(testRun.createdAt.seconds * 1000)) : 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                isIconOnly
                                                variant="light"
                                                color="danger"
                                                onPress={() => handleDelete(testRun.id!)}
                                            >
                                                <AiOutlineDelete />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )
                            }}
                        </TableBody>
                    </Table>
                </div>
                <div className="flex-1 overflow-y-auto p-4 bg-base-100 mt-2">
                    {selectedTestRun ? (
                        <>
                            <div className="flex-col mt-5">
                                <div className="text-lg font-bold">Schema</div>
                                <span className="text-sm">The schema used for this test run</span>
                            </div>
                            <div className="mt-2 pointer-events-none opacity-75">
                                <Tree onSchemaChange={() => {}} schema={selectedTestRun.schema} />
                            </div>
                            <div className="flex-col mt-5">
                                <div className="text-lg font-bold">Output</div>
                                <span className="text-sm">Response from the extractor for this test run</span>
                            </div>
                            <div className="flex flex-col mt-5">
                                {_showOutputs()}
                            </div>
                        </>
                    ) : (
                        <div className="flex justify-center items-center h-full text-gray-500">
                            Select a test run to view details
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

